import { Box, CircularProgress, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import {
  fetchCapacityPageData,
  saveArrivals,
  saveDepartures,
  savePriceStatus,
  exportPrices,
} from "./services";
import {
  AccommodationSearchOption,
  ArrivalRow,
  DepartureRow,
  PageData,
  Status,
  UhCatSearchOption,
} from "./types";
import AppButton from "../components/AppButton";
import FiltersBar from "./components/FiltersBar";
import UhCatRanking from "./components/UhCatRanking";
import ArrivalManagement from "./components/ArrivalManagement";
import DepartureManagement from "./components/DepartureManagement";
import EmptyStateCard from "../components/EmptyStateCard";
import AccordionSection from "../components/AccordionSection";

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 7 }, (_, i) => currentYear - 1 + i);

export default function CapacityCustomContent() {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const [accommodationId, setAccommodationId] = useState<number | "">("");
  const [uhCatId, setUhCatId] = useState<number | "">("");
  const [year, setYear] = useState<number>(currentYear);

  const [pageData, setPageData] = useState<PageData | null>(null);
  const [savedArrivals, setSavedArrivals] = useState<ArrivalRow[]>([]);
  const [savedDepartures, setSavedDepartures] = useState<DepartureRow[]>([]);

  const [arrivals, setArrivals] = useState<ArrivalRow[]>([]);
  const [departures, setDepartures] = useState<DepartureRow[]>([]);

  const [price, setPrice] = useState("");
  const [savedPrice, setSavedPrice] = useState("");
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [selectedStatusId, setSelectedStatusId] = useState<number | "">("");
  const [savedStatusId, setSavedStatusId] = useState<number | "">("");

  const [arrivalsIsDirty, setArrivalsIsDirty] = useState(false);
  const [departuresIsDirty, setDeparturesIsDirty] = useState(false);
  const [priceStatusIsDirty, setPriceStatusIsDirty] = useState(false);

  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSavingArrivals, setIsSavingArrivals] = useState(false);
  const [isSavingDepartures, setIsSavingDepartures] = useState(false);
  const [isSavingPriceStatus, setIsSavingPriceStatus] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const applyPageData = useCallback((data: PageData) => {
    setPageData(data);
    setArrivals(data.arrivals);
    setSavedArrivals(data.arrivals);
    setDepartures(data.departures);
    setSavedDepartures(data.departures);
    setPrice(data.price);
    setSavedPrice(data.price);
    setStatuses(data.statuses);
    const statusId = data.selectedStatusId ?? "";
    setSelectedStatusId(statusId);
    setSavedStatusId(statusId);
    setArrivalsIsDirty(false);
    setDeparturesIsDirty(false);
    setPriceStatusIsDirty(false);
  }, []);

  const loadData = useCallback(
    async (accommodationIdVal: number, uhCatIdVal: number, yearVal: number) => {
      setIsLoadingData(true);
      setPageData(null);
      try {
        const data = await fetchCapacityPageData(
          accommodationIdVal,
          uhCatIdVal,
          yearVal
        );
        applyPageData(data);
      } catch (e: any) {
        enqueueSnackbar(e.message ?? "Erreur lors du chargement des données", {
          variant: "error",
        });
      } finally {
        setIsLoadingData(false);
      }
    },
    [applyPageData, enqueueSnackbar]
  );

  const handleAccommodationChange = (
    accommodation: AccommodationSearchOption | null
  ) => {
    setAccommodationId(accommodation?.id ?? "");
    setUhCatId("");
    setPageData(null);
  };

  // The UH cat can now be picked directly (by name or id) without first
  // selecting a camping; the option carries its parent camping, so we set both
  // and load (legacy parity).
  const handleUhCatSelect = (option: UhCatSearchOption | null) => {
    if (!option) {
      setUhCatId("");
      setPageData(null);
      return;
    }
    setAccommodationId(option.hebergementId);
    setUhCatId(option.id);
    void loadData(option.hebergementId, option.id, year);
  };

  const handleYearChange = (val: number) => {
    setYear(val);
    if (uhCatId !== "" && accommodationId !== "") {
      void loadData(accommodationId as number, uhCatId as number, val);
    }
  };

  const handleSaveArrivals = async () => {
    if (uhCatId === "") return;
    setIsSavingArrivals(true);
    try {
      const result = await saveArrivals(uhCatId as number, year, arrivals);
      setSavedArrivals(arrivals);
      setDepartures(result.departures);
      setSavedDepartures(result.departures);
      setPrice(result.price);
      setSavedPrice(result.price);
      setStatuses(result.statuses);
      const statusId = result.selectedStatusId ?? "";
      setSelectedStatusId(statusId);
      setSavedStatusId(statusId);
      setArrivalsIsDirty(false);
      setDeparturesIsDirty(false);
      enqueueSnackbar("Mise à jour réussie", { variant: "success" });
    } catch (e: any) {
      enqueueSnackbar(e.message ?? "Erreur", { variant: "error" });
    } finally {
      setIsSavingArrivals(false);
    }
  };

  const handleSaveDepartures = async () => {
    if (uhCatId === "") return;
    setIsSavingDepartures(true);
    try {
      const result = await saveDepartures(uhCatId as number, year, departures);
      setSavedDepartures(departures);
      setDeparturesIsDirty(false);
      if (result.statuses.length > 0) {
        setStatuses(result.statuses);
        const statusId = result.selectedStatusId ?? "";
        setSelectedStatusId(statusId);
        setSavedStatusId(statusId);
      }
      enqueueSnackbar("Mise à jour réussie", { variant: "success" });
    } catch (e: any) {
      enqueueSnackbar(e.message ?? "Erreur", { variant: "error" });
    } finally {
      setIsSavingDepartures(false);
    }
  };

  const handleSavePriceStatus = async () => {
    if (uhCatId === "") return;
    setIsSavingPriceStatus(true);
    try {
      await savePriceStatus(
        uhCatId as number,
        year,
        price,
        selectedStatusId === "" ? null : (selectedStatusId as number)
      );
      setSavedPrice(price);
      setSavedStatusId(selectedStatusId);
      setPriceStatusIsDirty(false);
      enqueueSnackbar("Prix et statut mis à jour", { variant: "success" });
    } catch (e: any) {
      enqueueSnackbar(e.message ?? "Erreur", { variant: "error" });
    } finally {
      setIsSavingPriceStatus(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportPrices();
      enqueueSnackbar(
        intl.formatMessage({ id: "FILE_MANAGER.EXPORT_STARTED" }),
        { variant: "success" }
      );
    } catch (e: any) {
      enqueueSnackbar(e.message ?? "Erreur lors de l'export", {
        variant: "error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box sx={{ width: "100%", marginBottom: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {intl.formatMessage({ id: "CAPACITY_TITLE_SETUP" })}
      </Typography>

      <FiltersBar
        onUhCatSelect={handleUhCatSelect}
        onAccommodationChange={handleAccommodationChange}
        year={year}
        onYearChange={handleYearChange}
        years={YEARS}
        pageData={pageData}
        price={price}
        onPriceChange={(p) => {
          setPrice(p);
          setPriceStatusIsDirty(true);
        }}
        statuses={statuses}
        selectedStatusId={selectedStatusId}
        onStatusChange={(id) => {
          setSelectedStatusId(id);
          setPriceStatusIsDirty(true);
        }}
        priceStatusIsDirty={priceStatusIsDirty}
        onSavePriceStatus={handleSavePriceStatus}
        onResetPriceStatus={() => {
          setPrice(savedPrice);
          setSelectedStatusId(savedStatusId);
          setPriceStatusIsDirty(false);
        }}
        isSavingPriceStatus={isSavingPriceStatus}
        isExporting={isExporting}
        onExport={handleExport}
      />

      {!isLoadingData && pageData === null && (
        <EmptyStateCard
          logo={<TuneRoundedIcon sx={{ fontSize: 40 }} color="primary" />}
          text={intl.formatMessage({ id: "CAPACITE_CUSTOM_EMPTY_TITLE" })}
          subText={intl.formatMessage({
            id: "CAPACITE_CUSTOM_EMPTY_DESCRIPTION",
          })}
        />
      )}

      {isLoadingData && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoadingData && pageData && (
        <Box>
          <AccordionSection
            label="CAPACITE_CUSTOM_ARRIVALS_SECTION"
            actions={
              <>
                <AppButton
                  size="small"
                  onClick={handleSaveArrivals}
                  disabled={!arrivalsIsDirty || isSavingArrivals}
                  loading={isSavingArrivals}
                >
                  {intl.formatMessage({ id: "SUBMIT" })}
                </AppButton>
                <AppButton
                  intent="secondary"
                  size="small"
                  onClick={() => {
                    setArrivals(savedArrivals);
                    setArrivalsIsDirty(false);
                  }}
                  disabled={!arrivalsIsDirty || isSavingArrivals}
                >
                  {intl.formatMessage({ id: "RESET" })}
                </AppButton>
              </>
            }
          >
            <Box>
              <ArrivalManagement
                rows={arrivals}
                onChange={(rows) => {
                  setArrivals(rows);
                  setArrivalsIsDirty(true);
                }}
                year={year}
              />
            </Box>
          </AccordionSection>

          {departures.length > 0 && (
            <AccordionSection
              label="CAPACITE_CUSTOM_DEPARTURES_SECTION"
              actions={
                <>
                  <AppButton
                    size="small"
                    onClick={handleSaveDepartures}
                    disabled={!departuresIsDirty || isSavingDepartures}
                    loading={isSavingDepartures}
                  >
                    {intl.formatMessage({ id: "SUBMIT" })}
                  </AppButton>
                  <AppButton
                    intent="secondary"
                    size="small"
                    onClick={() => {
                      setDepartures(savedDepartures);
                      setDeparturesIsDirty(false);
                    }}
                    disabled={!departuresIsDirty || isSavingDepartures}
                  >
                    {intl.formatMessage({ id: "RESET" })}
                  </AppButton>
                </>
              }
            >
              <Box>
                <DepartureManagement
                  rows={departures}
                  onChange={(rows) => {
                    setDepartures(rows);
                    setDeparturesIsDirty(true);
                  }}
                />
              </Box>
            </AccordionSection>
          )}

          {pageData.uhCats.length > 0 &&
            (arrivals.length > 0 || departures.length > 0) && (
              <AccordionSection label="CAPACITE_CUSTOM_UHCAT_RANKING_SECTION">
                <Box>
                  <UhCatRanking
                    uhCats={pageData.uhCats}
                    onReordered={(updated) =>
                      setPageData((prev) =>
                        prev ? { ...prev, uhCats: updated } : prev
                      )
                    }
                    accommodationId={accommodationId as number}
                  />
                </Box>
              </AccordionSection>
            )}
        </Box>
      )}
    </Box>
  );
}
